import { useMemo, useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, CalendarDays, CheckCircle2, Download, Plus, Repeat2, WalletCards } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { formatCurrencyInput, parseCurrencyInput } from "@/lib/inputFormatters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const money = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const statusMeta: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendente", className: "bg-amber-100 text-amber-800 border-amber-200" },
  paid: { label: "Quitada", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Cancelada", className: "bg-muted text-muted-foreground border-border" },
};

function toDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(offset: number) {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return toDateInput(date);
}

function csvValue(value: unknown) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

export default function AdminFinancial() {
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "payable" | "receivable">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "paid" | "cancelled">("all");
  const [dueFilter, setDueFilter] = useState<"all" | "overdue" | "next7" | "month">("all");
  const [form, setForm] = useState({
    type: "payable" as "payable" | "receivable",
    description: "",
    amount: "",
    dueDate: toDateInput(new Date()),
    category: "",
    contactName: "",
    isRecurring: false,
    recurrenceIntervalDays: "30",
    notes: "",
  });

  const today = toDateInput(new Date());
  const listInput = useMemo(() => {
    const base = {
      ...(filter === "all" ? {} : { type: filter }),
      ...(statusFilter === "all" ? {} : { status: statusFilter }),
    };
    if (dueFilter === "overdue") return { ...base, status: "pending" as const, dueTo: addDays(-1) };
    if (dueFilter === "next7") return { ...base, status: "pending" as const, dueFrom: today, dueTo: addDays(7) };
    if (dueFilter === "month") {
      const now = new Date();
      return {
        ...base,
        dueFrom: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
        dueTo: toDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    }
    return base;
  }, [filter, statusFilter, dueFilter, today]);

  const { data: entries = [], isLoading } = (trpc as any).financial.list.useQuery(listInput);
  const priorityInput = useMemo(() => ({ status: "pending", dueTo: addDays(7) }), [today]);
  const { data: priorityEntries = [] } = (trpc as any).financial.list.useQuery(priorityInput);
  const { data: summary } = (trpc as any).financial.summary.useQuery();
  const create = (trpc as any).financial.create.useMutation({
    onSuccess: () => {
      toast.success("Conta lançada no financeiro.");
      setOpen(false);
      setForm({ type: "payable", description: "", amount: "", dueDate: toDateInput(new Date()), category: "", contactName: "", isRecurring: false, recurrenceIntervalDays: "30", notes: "" });
      utils.invalidate();
    },
    onError: (error: Error) => toast.error(error.message || "Não foi possível lançar a conta."),
  });
  const settle = (trpc as any).financial.settle.useMutation({
    onSuccess: () => {
      toast.success("Conta quitada. Contas recorrentes geram automaticamente a próxima ocorrência.");
      utils.invalidate();
    },
    onError: (error: Error) => toast.error(error.message || "Não foi possível quitar a conta."),
  });

  const submit = () => {
    const amount = parseCurrencyInput(form.amount);
    if (!form.description.trim() || amount <= 0 || !form.dueDate) {
      toast.error("Preencha descrição, valor e vencimento.");
      return;
    }
    create.mutate({
      type: form.type,
      description: form.description.trim(),
      amount: amount.toFixed(2),
      dueDate: form.dueDate,
      category: form.category.trim() || undefined,
      contactName: form.contactName.trim() || undefined,
      isRecurring: form.isRecurring,
      recurrenceIntervalDays: form.isRecurring ? Number(form.recurrenceIntervalDays) : undefined,
      notes: form.notes.trim() || undefined,
    });
  };

  const exportEntries = () => {
    if (!entries.length) {
      toast.error("Não há contas para exportar com os filtros selecionados.");
      return;
    }
    const header = ["Tipo", "Descrição", "Categoria", "Contato", "Vencimento", "Status", "Valor", "Recorrente", "Observações"];
    const rows = entries.map((entry: any) => [
      entry.type === "payable" ? "A pagar" : "A receber",
      entry.description,
      entry.category || "",
      entry.contactName || "",
      entry.dueDate,
      statusMeta[entry.status]?.label ?? entry.status,
      Number(entry.amount).toFixed(2).replace(".", ","),
      entry.isRecurring ? "Sim" : "Não",
      entry.notes || "",
    ]);
    const csv = [header, ...rows].map((row) => row.map(csvValue).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `fechamento-financeiro-${dueFilter}-${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const balance = Number(summary?.paidReceivable ?? 0) - Number(summary?.paidPayable ?? 0);
  const projectedBalance = balance + Number(summary?.pendingReceivable ?? 0) - Number(summary?.pendingPayable ?? 0);
  const paymentPriority = useMemo(() => {
    const overdue = priorityEntries.filter((entry: any) => entry.dueDate < today);
    const dueToday = priorityEntries.filter((entry: any) => entry.dueDate === today);
    const upcoming = priorityEntries.filter((entry: any) => entry.dueDate > today);
    const totalAtRisk = [...overdue, ...dueToday].reduce((total: number, entry: any) => total + Number(entry.amount ?? 0), 0);
    return { overdue, dueToday, upcoming, totalAtRisk };
  }, [priorityEntries, today]);
  const nextWeekCashFlow = useMemo(() => {
    const currentAndUpcoming = [...paymentPriority.dueToday, ...paymentPriority.upcoming];
    const receivable = currentAndUpcoming.filter((entry: any) => entry.type === "receivable").reduce((total: number, entry: any) => total + Number(entry.amount ?? 0), 0);
    const payable = currentAndUpcoming.filter((entry: any) => entry.type === "payable").reduce((total: number, entry: any) => total + Number(entry.amount ?? 0), 0);
    return { receivable, payable, net: receivable - payable };
  }, [paymentPriority]);
  const typeFilters = [{ value: "all", label: "Todas" }, { value: "payable", label: "A pagar" }, { value: "receivable", label: "A receber" }] as const;
  const statusFilters = [{ value: "all", label: "Todas as situações" }, { value: "pending", label: "Pendentes" }, { value: "paid", label: "Quitadas" }, { value: "cancelled", label: "Canceladas" }] as const;
  const dueFilters = [{ value: "all", label: "Todos os vencimentos" }, { value: "overdue", label: "Vencidas" }, { value: "next7", label: "Próximos 7 dias" }, { value: "month", label: "Este mês" }] as const;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Financeiro</h1>
          <p className="mt-1 text-sm text-muted-foreground">Acompanhe contas a pagar, receber, vencimentos e o saldo do período.</p>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={exportEntries}><Download className="h-4 w-4 mr-2" />Exportar CSV</Button>
          <Button className="flex-1 sm:flex-none" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-2" />Lançar conta</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">A receber pendente</p><p className="mt-1 text-xl font-semibold text-emerald-700">{money(Number(summary?.pendingReceivable ?? 0))}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">A pagar pendente</p><p className="mt-1 text-xl font-semibold text-rose-700">{money(Number(summary?.pendingPayable ?? 0))}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Vencido</p><p className="mt-1 text-xl font-semibold text-amber-700">{money(Number(summary?.overdue ?? 0))}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Saldo quitado</p><p className={`mt-1 text-xl font-semibold ${balance >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(balance)}</p></CardContent></Card>
        <Card className="border-primary/20 bg-primary/5"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Saldo projetado</p><p className={`mt-1 text-xl font-semibold ${projectedBalance >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(projectedBalance)}</p><p className="mt-1 text-[11px] leading-4 text-muted-foreground">Considera pendências a pagar e receber.</p></CardContent></Card>
      </div>

      {(paymentPriority.overdue.length > 0 || paymentPriority.dueToday.length > 0 || paymentPriority.upcoming.length > 0) && <Card className={paymentPriority.overdue.length > 0 ? "border-amber-300 bg-amber-50/40" : "border-sky-200 bg-sky-50/40"}><CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm font-semibold">Prioridade financeira</p><p className="mt-1 text-xs text-muted-foreground">{paymentPriority.overdue.length > 0 ? `${paymentPriority.overdue.length} conta(s) vencida(s)` : "Nenhuma conta vencida"} · {paymentPriority.dueToday.length} vence(m) hoje · {paymentPriority.upcoming.length} vence(m) nos próximos 7 dias.</p>{paymentPriority.totalAtRisk > 0 && <p className="mt-1 text-xs font-medium text-amber-800">Valor em atraso ou com vencimento hoje: {money(paymentPriority.totalAtRisk)}</p>}<div className="mt-3 grid gap-2 text-xs sm:grid-cols-3"><div className="rounded-md border border-sky-200 bg-background/80 p-2"><p className="text-muted-foreground">A receber em 7 dias</p><p className="mt-0.5 font-semibold text-emerald-700">{money(nextWeekCashFlow.receivable)}</p></div><div className="rounded-md border border-sky-200 bg-background/80 p-2"><p className="text-muted-foreground">A pagar em 7 dias</p><p className="mt-0.5 font-semibold text-rose-700">{money(nextWeekCashFlow.payable)}</p></div><div className="rounded-md border border-sky-200 bg-background/80 p-2"><p className="text-muted-foreground">Fluxo líquido em 7 dias</p><p className={`mt-0.5 font-semibold ${nextWeekCashFlow.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(nextWeekCashFlow.net)}</p></div></div></div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setDueFilter(paymentPriority.overdue.length > 0 ? "overdue" : "next7")}>{paymentPriority.overdue.length > 0 ? "Revisar vencidas" : "Ver próximos vencimentos"}</Button></div></CardContent></Card>}

      <div className="space-y-2">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {typeFilters.map((item) => <Button key={item.value} size="sm" variant={filter === item.value ? "default" : "outline"} onClick={() => setFilter(item.value)}>{item.label}</Button>)}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {statusFilters.map((item) => <Button key={item.value} size="sm" variant={statusFilter === item.value ? "default" : "outline"} onClick={() => setStatusFilter(item.value)}>{item.label}</Button>)}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {dueFilters.map((item) => <Button key={item.value} size="sm" variant={dueFilter === item.value ? "default" : "outline"} className={item.value === "overdue" && dueFilter !== item.value ? "border-amber-300 text-amber-700" : ""} onClick={() => setDueFilter(item.value)}><CalendarDays className="mr-1 h-3.5 w-3.5" />{item.label}</Button>)}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><WalletCards className="h-5 w-5 text-primary" />Contas lançadas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? <p className="py-10 text-center text-sm text-muted-foreground">Carregando contas...</p> : entries.length === 0 ? (
            <div className="py-10 text-center"><WalletCards className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" /><p className="font-medium">Nenhuma conta lançada</p><p className="mt-1 text-sm text-muted-foreground">Registre despesas, recebimentos e compromissos recorrentes para acompanhar o fluxo de caixa.</p></div>
          ) : entries.map((entry: any) => {
            const meta = statusMeta[entry.status] ?? statusMeta.pending;
            const overdue = entry.status === "pending" && entry.dueDate < today;
            const dueInDays = Math.round((new Date(`${entry.dueDate}T12:00:00`).getTime() - new Date(`${today}T12:00:00`).getTime()) / 86_400_000);
            const dueSoon = entry.status === "pending" && dueInDays >= 0 && dueInDays <= 7;
            return <div key={entry.id} className={`flex flex-col gap-3 rounded-xl border p-4 md:flex-row md:items-center md:justify-between ${overdue ? "border-amber-300 bg-amber-50/40" : dueSoon ? "border-sky-200 bg-sky-50/40" : ""}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{entry.description}</p>
                  <Badge variant="outline" className={meta.className}>{overdue ? "Vencida" : meta.label}</Badge>
                  {dueSoon && !overdue && <Badge variant="outline" className="border-sky-200 bg-sky-100 text-sky-800">{dueInDays === 0 ? "Vence hoje" : `Vence em ${dueInDays} dia${dueInDays > 1 ? "s" : ""}`}</Badge>}
                  {entry.isRecurring && <Badge variant="outline"><Repeat2 className="h-3 w-3 mr-1" />Recorrente</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{entry.type === "payable" ? "A pagar" : "A receber"} · {entry.category || "Sem categoria"} · vencimento {new Date(`${entry.dueDate}T12:00:00`).toLocaleDateString("pt-BR")}</p>
              </div>
              <div className="flex items-center gap-3">
                <p className={`font-semibold ${entry.type === "payable" ? "text-rose-700" : "text-emerald-700"}`}>{entry.type === "payable" ? "−" : "+"}{money(Number(entry.amount))}</p>
                {entry.status === "pending" && <Button size="sm" variant="outline" onClick={() => settle.mutate({ id: entry.id })}><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Quitar</Button>}
              </div>
            </div>;
          })}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>Lançar conta financeira</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div><Label>Tipo</Label><Select value={form.type} onValueChange={(type: "payable" | "receivable") => setForm({ ...form, type })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="payable"><span className="flex items-center gap-2"><ArrowDownCircle className="h-4 w-4 text-rose-600" />Conta a pagar</span></SelectItem><SelectItem value="receivable"><span className="flex items-center gap-2"><ArrowUpCircle className="h-4 w-4 text-emerald-600" />Conta a receber</span></SelectItem></SelectContent></Select></div>
            <div className="grid gap-4 sm:grid-cols-2"><div><Label>Descrição</Label><Input className="mt-1" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Ex.: Aluguel" /></div><div><Label>Valor</Label><Input className="mt-1" inputMode="decimal" placeholder="R$ 0,00" value={form.amount} onChange={(event) => setForm({ ...form, amount: formatCurrencyInput(event.target.value) })} /></div></div>
            <div className="grid gap-4 sm:grid-cols-2"><div><Label>Vencimento</Label><Input className="mt-1" type="date" value={form.dueDate} onChange={(event) => setForm({ ...form, dueDate: event.target.value })} /></div><div><Label>Categoria</Label><Input className="mt-1" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} placeholder="Ex.: Fornecedor" /></div></div>
            <div><Label>Contato / fornecedor (opcional)</Label><Input className="mt-1" value={form.contactName} onChange={(event) => setForm({ ...form, contactName: event.target.value })} /></div>
            <label className="flex items-center gap-2 rounded-lg border p-3 text-sm"><input type="checkbox" checked={form.isRecurring} onChange={(event) => setForm({ ...form, isRecurring: event.target.checked })} />Esta é uma conta recorrente</label>
            {form.isRecurring && <div><Label>Repetir a cada quantos dias?</Label><Input className="mt-1" type="number" min="1" max="365" value={form.recurrenceIntervalDays} onChange={(event) => setForm({ ...form, recurrenceIntervalDays: event.target.value })} /></div>}
            <div><Label>Observações</Label><Textarea className="mt-1" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={submit} disabled={create.isPending}>{create.isPending ? "Salvando..." : "Lançar conta"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
